import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/api";
import { Plus, Minus, Trash2, ShoppingBag } from "lucide-react";

export default function CartDrawer({ open, onClose, items, onQty, onRemove, onCheckout, onAddMore, tableNumber, t, submitting, theme = { primary: "#123026", gold: "#C69C4A" } }) {
  const subtotal = items.reduce((s, i) => s + i.line_total, 0);

  const optionLabels = (i) => {
    const parts = [];
    (i.formula || []).forEach((f) => parts.push(`${f.course} : ${f.choice}`));
    (i.options || []).forEach((o) => parts.push(o.value));
    if (i.cooking) parts.push(i.cooking);
    if (i.side) parts.push(i.side);
    (i.supplements || []).forEach((s) => parts.push(s.name));
    if (i.instructions) parts.push(`✎ ${i.instructions}`);
    return parts;
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" data-testid="cart-drawer" className="p-0 rounded-t-3xl border-none bg-[#FBF9F5] max-h-[85vh] flex flex-col">
        <SheetTitle className="sr-only">{t.yourOrder}</SheetTitle>
        <SheetDescription className="sr-only">Récapitulatif de votre commande table {tableNumber}</SheetDescription>
        <div className="px-5 pt-5 pb-3 border-b border-[#EAE5DC]">
          <h2 className="font-serif-display text-2xl text-[#123026]">{t.yourOrder}</h2>
          <p className="text-sm text-[#8A918D] mt-0.5">{t.table} {tableNumber}</p>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center py-16 px-6 text-center">
            <div>
              <div className="w-14 h-14 rounded-full bg-[#EBF2EE] grid place-items-center mx-auto mb-4">
                <ShoppingBag className="w-6 h-6 text-[#123026]" />
              </div>
              <p className="font-semibold text-[#123026]">{t.emptyCart}</p>
              <p className="text-sm text-[#8A918D] mt-1">{t.emptyCartHint}</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-4 space-y-3">
            {items.map((i, idx) => (
              <div key={i.cart_id} data-testid={`cart-item-${idx}`} className="flex gap-3 rounded-2xl bg-white border border-[#EAE5DC] p-3">
                <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <p className="text-sm font-semibold text-[#123026]">{i.name}</p>
                    <p className="text-sm font-bold text-[#123026] whitespace-nowrap">{eur(i.line_total)}</p>
                  </div>
                  {optionLabels(i).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {optionLabels(i).map((o, k) => (
                        <li key={k} className="text-xs text-[#8A918D]">• {o}</li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 rounded-full border border-[#EAE5DC] px-1.5 py-1">
                      <button data-testid={`cart-qty-dec-${idx}`} onClick={() => onQty(i.cart_id, -1)} className="w-6 h-6 grid place-items-center text-[#123026]">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-4 text-center text-sm font-bold text-[#123026]">{i.quantity}</span>
                      <button data-testid={`cart-qty-inc-${idx}`} onClick={() => onQty(i.cart_id, 1)} className="w-6 h-6 grid place-items-center text-[#123026]">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button data-testid={`cart-remove-${idx}`} onClick={() => onRemove(i.cart_id)} className="text-[#8A918D] hover:text-[#DC2626] p-1.5">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <Button
              data-testid="cart-add-more"
              onClick={onAddMore}
              variant="outline"
              className="w-full rounded-full border-[#123026] text-[#123026] hover:bg-[#EBF2EE] font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> {t.addMore}
            </Button>
          </div>
        )}

        {items.length > 0 && (
          <div className="shrink-0 border-t border-[#EAE5DC] px-5 py-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#5C615E]">{t.subtotal}</span>
              <span data-testid="cart-subtotal" className="text-lg font-bold text-[#123026]">{eur(subtotal)}</span>
            </div>
            <Button
              data-testid="cart-checkout"
              onClick={onCheckout}
              disabled={submitting}
              className="w-full h-13 py-3.5 rounded-full text-[#0C1713] font-bold text-base"
              style={{ backgroundColor: theme.gold }}
            >
              {submitting ? "..." : `${t.order} · ${eur(subtotal)}`}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
